import { formatUtcDate } from '@/helpers/formatUtcDate';
import type { DateRangeQueryOptions } from './visibility-range';

export function appendSourceIdsParam(params: URLSearchParams, sourceIds?: string[]): void {
  if (sourceIds?.length) {
    params.set('sourceIds', sourceIds.join(','));
  }
}

export function buildSourceInsightsQuery(options?: DateRangeQueryOptions): string {
  const params = new URLSearchParams();
  if (options?.topicId) params.set('topicId', options.topicId);
  if (options?.range) {
    params.set('range', options.range);
    if (options.range === 'custom' && options.from && options.to) {
      params.set('from', options.from);
      params.set('to', options.to);
    }
  }
  if (options?.providerIds?.length) {
    params.set('providerIds', options.providerIds.join(','));
  }
  appendSourceIdsParam(params, options?.sourceIds);
  const query = params.toString();
  return query ? `?${query}` : '';
}

const DISPLAY_TYPE_LABELS: Record<string, string> = {
  You: 'شما',
  Competitor: 'رقیب',
  UGC: 'UGC',
  Corporate: 'شرکتی',
  Editorial: 'تحریریه',
  Reference: 'مرجع',
  Institutional: 'نهادی',
  Marketplace: 'بازارگاه',
  Directory: 'دایرکتوری',
  Other: 'سایر',
  Unknown: 'نامشخص',
};

export function formatSourceDisplayType(type: string | null | undefined): string {
  if (!type) return '—';
  return DISPLAY_TYPE_LABELS[type] ?? type;
}

export function formatSourcePercent(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return `${value}%`;
}

export function formatSourceCount(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return String(Math.round(value));
}

export function formatSourceDate(value: string | null | undefined): string {
  return formatUtcDate(value, { preset: 'dateTime' }) || '—';
}

export function sourceDisplayLabel(host: string, displayName?: string | null): string {
  return displayName?.trim() || host;
}

export function buildBrandSourceInsightsPath(
  projectId: string,
  brandKey: string,
  options?: DateRangeQueryOptions,
): string {
  return `/projects/${projectId}/source-insights/brands/${encodeURIComponent(brandKey)}${buildSourceInsightsQuery(options)}`;
}

export function buildCompetitorSourceGapPath(
  projectId: string,
  options?: DateRangeQueryOptions,
): string {
  return `/projects/${projectId}/source-insights/competitor-gap${buildSourceInsightsQuery(options)}`;
}

const GAP_STATUS_LABELS: Record<string, string> = {
  competitor_only: 'فقط رقبا',
  you_underrepresented: 'شما کمتر دیده شده‌اید',
  shared: 'مشترک',
};

export function formatSourceGapStatus(gapType: string | null | undefined): string {
  if (!gapType) return '—';
  return GAP_STATUS_LABELS[gapType] ?? gapType;
}

export function formatBrandSourcesEmptyState(): string {
  return 'برای این برند هنوز منبعی استخراج نشده است.';
}
